import { EntityKind, type CollisionGrid, type EdgeViewRecord, type EntityState } from "odyssey-shared";
import type { EdgeNearFrame } from "../handoff-wire.ts";
import type { DrawnCharacter, RemoteCharacters } from "../remote-characters.ts";
import { BESIDE_HANDLE_BASE, BESIDE_HANDLES, type CharacterVisitor } from "../world-ui/characters.ts";
import { EdgeBook, type BookMap, type OwnName } from "./edge-book.ts";
import { CLAIM_LATER, FigureHold, nameKey, type Claimed } from "./hold.ts";
import { entityKey, keyMapIndex } from "./reach.ts";

/**
 * How many figures a crossing may hold at once: a view's worth of a busy seam, both sides of it.
 */
const HOLD_CAPACITY = 128;

/**
 * What the figures are drawn with: the world's maps and version, a new collection for a neighbour's figures, and the
 * clock the hold runs on (the game loop's time, as the figures' poses are).
 */
export interface EdgeFiguresOptions {
  readonly maps: readonly BookMap[];
  readonly version: string;
  readonly make: () => RemoteCharacters;
  readonly now: () => number;
}

/**
 * The figures of the other maps' streams on the page (ADR-147 decisions 7, 13, 15 and 19): each neighbour's edge view
 * drawn by its own collection at its own rate and clock, the entities filed under `entityKey(mapIndex, handle)` by the
 * book, and the figures a crossing holds. A figure that leaves one stream (a `crossed` record of the owning map's
 * stream, a player an edge view or a keyframe no longer carries, and every figure when the page's own character crosses)
 * is held on its last motion for up to `CROSSED_HOLD_MS` and taken back, the same figure, by whichever collection the
 * same character turns up in: by its key on the same map, by its name on the other. So a watcher sees one continuous
 * figure. Edge figures live apart from the owner's collection, so nothing can target them for an attack (decision 24).
 */
export class EdgeFigures {
  public readonly book: EdgeBook;
  private readonly hold: FigureHold<DrawnCharacter>;
  private readonly collections = new Map<number, RemoteCharacters>();
  private readonly heldStates = new Map<DrawnCharacter, { readonly key: number; readonly state: EntityState }>();
  private readonly plates = new Map<DrawnCharacter, { readonly id: number; seen: number }>();
  private readonly freePlates: number[] = Array.from({ length: BESIDE_HANDLES }, (_, i) => BESIDE_HANDLE_BASE + BESIDE_HANDLES - 1 - i);
  private visits = 0;
  /** The frame `draw` hands each collection, filled in place so a frame allocates nothing. */
  private readonly frame = { delta: 0, localX: 0, localY: 0, renderedX: 0, renderedY: 0 };
  /** The visitor `visit` walks the figures for, set for the length of the walk. */
  private visitor: CharacterVisitor | null = null;
  /** The entities of the neighbour `visitCollection` walks, set for the length of its walk. */
  private visitedEntities: ReadonlyMap<number, EntityState> | null = null;
  /**
   * Draws one neighbour's collection at the frame `draw` filled.
   *
   * @param collection The collection.
   * @param index The neighbour's manifest index.
   */
  private readonly drawOne = (collection: RemoteCharacters, index: number): void => {
    const frame = this.frame;
    collection.draw(frame.delta, this.time, this.scale, this.book.entities(index), -1, frame.localX, frame.localY, frame.renderedX, frame.renderedY);
  };
  /**
   * Walks one neighbour's drawn figures for the visitor.
   *
   * @param collection The collection.
   * @param index The neighbour's manifest index.
   */
  private readonly visitCollection = (collection: RemoteCharacters, index: number): void => {
    this.visitedEntities = this.book.entities(index);
    collection.drawn.forEach(this.visitFigure);
    this.visitedEntities = null;
  };
  /**
   * Hands one neighbour figure to the visitor under its plate key, with the state its view holds.
   *
   * @param figure The figure.
   * @param key Its `entityKey`.
   */
  private readonly visitFigure = (figure: DrawnCharacter, key: number): void => {
    const state = this.visitedEntities?.get(key);
    const plate = state === undefined ? -1 : this.plateOf(figure);
    if (state !== undefined && plate >= 0) {
      this.visitor?.(plate, figure.drawnX, figure.drawnY, state, figure.head + figure.sprite.lift, figure.hidden || figure.entering);
    }
  };
  /**
   * Hands one held figure to the visitor under its plate key, with its last state.
   *
   * @param held Its key and last state.
   * @param figure The figure.
   */
  private readonly visitHeld = (held: { readonly key: number; readonly state: EntityState }, figure: DrawnCharacter): void => {
    const plate = this.plateOf(figure);
    if (plate >= 0) {
      this.visitor?.(plate, figure.drawnX, figure.drawnY, held.state, figure.head + figure.sprite.lift, false);
    }
  };  /**
   * Gives back the plate key of a figure the last visit did not reach.
   *
   * @param plate The key and the visit that last saw it.
   * @param figure The figure.
   */
  private readonly sweepPlate = (plate: { readonly id: number; seen: number }, figure: DrawnCharacter): void => {
    if (plate.seen !== this.visits) {
      this.plates.delete(figure);
      this.freePlates.push(plate.id);
    }
  };
  private owner: RemoteCharacters | null = null;
  private ownerIndex = -1;
  private grid: CollisionGrid | null = null;
  private scale = 1;
  private time = 0;
  private readonly pose = { x: 0, y: 0, facing: 0 as DrawnCharacter["facing"], moving: false, running: false };
  /**
   * The claim a neighbour's collection asks for an entity it first draws, by the key it carries as its handle.
   *
   * @param state The entity.
   * @param depth The records its interpolator holds.
   * @returns The figure, `CLAIM_LATER`, or null.
   */
  private readonly claimEdge = (state: EntityState, depth: number): Readonly<Claimed<DrawnCharacter>> | typeof CLAIM_LATER | null => this.claimAt(state.handle, state, depth);
  /**
   * The claim the owning map's collection asks for an entity it first draws, by its key on the owning map.
   *
   * @param state The entity.
   * @param depth The records its interpolator holds.
   * @returns The figure, `CLAIM_LATER`, or null.
   */
  private readonly claimOwner = (state: EntityState, depth: number): Readonly<Claimed<DrawnCharacter>> | typeof CLAIM_LATER | null => this.claimAt(entityKey(Math.max(0, this.ownerIndex), state.handle), state, depth);
  /**
   * Poses one held figure where its last motion carries it.
   *
   * @param figure The figure.
   * @param x Across, in sixteenths.
   * @param y Down, in sixteenths.
   * @param moving Whether it moves.
   */
  private readonly poseHeld = (figure: DrawnCharacter, x: number, y: number, moving: boolean): void => {
    const pose = this.pose;
    figure.x = x;
    figure.y = y;
    figure.drawnX = Math.round(x * this.scale);
    figure.drawnY = Math.round(y * this.scale);
    pose.x = figure.drawnX;
    pose.y = figure.drawnY;
    pose.facing = figure.facing as DrawnCharacter["facing"];
    pose.moving = moving;
    pose.running = false;
    figure.sprite.setPose(pose as Parameters<DrawnCharacter["sprite"]["setPose"]>[0], this.time);
  };

  /**
   * @param options What the figures are drawn with.
   */
  public constructor(private readonly options: EdgeFiguresOptions) {
    this.book = new EdgeBook(options.maps, options.version, (key) => this.vanished(key));
    this.hold = new FigureHold<DrawnCharacter>(HOLD_CAPACITY, (figure) => {
      this.heldStates.delete(figure);
      figure.sprite.destroy();
    });
  }

  /**
   * How many figures a crossing holds now, for the report.
   *
   * @returns The count.
   */
  public get held(): number {
    return this.hold.size;
  }

  /**
   * Stands the figures on a map: the owning map's collection takes held figures by their key on that map, and later edge
   * views are read in its frame.
   *
   * @param mapId The owning map's id.
   * @param owner The owning map's collection.
   * @param grid The owning map's grid, which the neighbours' figures move against.
   */
  public stand(mapId: string, owner: RemoteCharacters, grid: CollisionGrid): void {
    this.ownerIndex = this.options.maps.findIndex((map) => map.id === mapId);
    this.owner = owner;
    this.grid = grid;
    owner.claim = this.claimOwner;
    this.book.stand(mapId);
    for (const collection of this.collections.values()) {
      collection.clear();
    }
    this.collections.clear();
  }

  /**
   * Takes an `edgeNear`.
   *
   * @param frame The frame.
   */
  public near(frame: EdgeNearFrame): void {
    this.book.near(frame);
  }

  /**
   * Takes one edge view: the book files it, and the neighbour's collection records it at the neighbour's tick.
   *
   * @param view The view, valid only during the call.
   * @param own The page's own character's name, or null before a welcome.
   */
  public receive(view: Readonly<EdgeViewRecord>, own: OwnName | null): void {
    if (this.grid === null || !this.book.receive(view, own)) {
      return;
    }
    let collection = this.collections.get(view.map);
    if (collection === undefined) {
      collection = this.options.make();
      collection.reportsLead = false;
      collection.claim = this.claimEdge;
      collection.begin(this.grid, view.tick);
      this.collections.set(view.map, collection);
    }
    collection.record(view.tick, this.book.entities(view.map), -1);
  }

  /**
   * Holds the figure of an entity that left the owning map's stream for another map (a `crossed` record), or that a
   * keyframe no longer carries, until the same character turns up elsewhere.
   *
   * @param handle Its handle in the owning map's stream.
   */
  public leftOwner(handle: number): void {
    const owner = this.owner;
    const state = owner?.stateOf(handle);
    const figure = owner?.surrender(handle);
    if (figure !== undefined) {
      this.offer(figure, entityKey(Math.max(0, this.ownerIndex), handle), state);
    }
  }

  /**
   * Holds every figure of every stream, as the page's own character crosses and the render origin moves: the owning map's
   * under their keys on it, each neighbour's under theirs, players under their names too.
   */
  public handOver(): void {
    const owner = this.owner;
    if (owner !== null) {
      for (const handle of [...owner.drawn.keys()]) {
        this.leftOwner(handle);
      }
    }
    for (const [index, collection] of this.collections) {
      const entities = this.book.entities(index);
      for (const key of [...collection.drawn.keys()]) {
        const figure = collection.surrender(key);
        if (figure !== undefined) {
          this.offer(figure, key, entities.get(key));
        }
      }
    }
  }

  /**
   * Moves every held figure by the change of the render origin.
   *
   * @param dx Across, in sixteenths.
   * @param dy Down, in sixteenths.
   */
  public shift(dx: number, dy: number): void {
    this.hold.shift(dx, dy);
  }

  /**
   * Draws every neighbour's figures at their own clocks, and the held figures on their last motion.
   *
   * @param delta The frame's delta, in milliseconds.
   * @param time The scene's time, in milliseconds.
   * @param scale World pixels a sixteenth.
   * @param localX Where the page's own character is drawn, across, in whole world pixels.
   * @param localY The same down.
   * @param renderedX Where it is rendered before rounding, across, in sixteenths.
   * @param renderedY The same down.
   */
  public draw(delta: number, time: number, scale: number, localX: number, localY: number, renderedX: number, renderedY: number): void {
    this.scale = scale;
    this.time = time;
    const frame = this.frame;
    frame.delta = delta;
    frame.localX = localX;
    frame.localY = localY;
    frame.renderedX = renderedX;
    frame.renderedY = renderedY;
    this.collections.forEach(this.drawOne);
    this.hold.step(this.options.now(), this.poseHeld);
  }

  /**
   * Hands every neighbour figure drawn, and every figure held, to a visitor with its state, for the nameplates and the
   * minimap. Each goes under a key from `BESIDE_HANDLE_BASE` on, past every handle of the owning map's stream, so nothing
   * keyed by handle (the target, the player menu) ever reaches it. The walk is by bound callbacks, so a frame allocates
   * nothing for it.
   *
   * @param visit The visitor.
   */
  public visit(visit: CharacterVisitor): void {
    this.visits += 1;
    this.visitor = visit;
    this.collections.forEach(this.visitCollection);
    this.heldStates.forEach(this.visitHeld);
    this.visitor = null;
    this.plates.forEach(this.sweepPlate);
  }



  /**
   * The key the world UI holds a figure's plate under while it is drawn here, kept from one visit to the next so its plate
   * stays its own; one no visit reaches is given back by the visit's sweep.
   *
   * @param figure The figure.
   * @returns The key, or -1 when every key is in use.
   */
  private plateOf(figure: DrawnCharacter): number {
    let plate = this.plates.get(figure);
    if (plate === undefined) {
      const id = this.freePlates.pop();
      if (id === undefined) {
        return -1;
      }
      plate = { id, seen: this.visits };
      this.plates.set(figure, plate);
    }
    plate.seen = this.visits;
    return plate.id;
  }

  /**
   * Hands where each neighbour figure and each held figure of a first name is drawn, for the report a browser suite reads
   * to see one figure through a crossing. Built when asked; not for a frame's work.
   *
   * @param first The first name.
   * @param found Told each figure's drawn point, in world pixels of the owning map's frame, and where it is drawn from.
   */
  public named(first: string, found: (x: number, y: number, from: "edge" | "held") => void): void {
    for (const [index, collection] of this.collections) {
      const entities = this.book.entities(index);
      for (const [key, figure] of collection.drawn) {
        if (entities.get(key)?.name.first === first) {
          found(figure.drawnX, figure.drawnY, "edge");
        }
      }
    }
    for (const [figure, held] of this.heldStates) {
      if (held.state.name.first === first) {
        found(figure.drawnX, figure.drawnY, "held");
      }
    }
  }

  /**
   * Lets every figure go, as a session ends.
   */
  public clear(): void {
    for (const collection of this.collections.values()) {
      collection.clear();
    }
    this.collections.clear();
    this.hold.clear();
    this.book.clear();
    if (this.owner !== null) {
      this.owner.claim = null;
    }
    this.owner = null;
    this.grid = null;
  }

  /**
   * An edge-view entity a view no longer carries, told while the book still holds its last state: a player is held, as one that crossed may turn up in the owning map's
   * stream a tick or two later (ADR-147 note 8 item a); any other entity never crosses and is let go as it would be.
   *
   * @param key Its key.
   */
  private vanished(key: number): void {
    for (const collection of this.collections.values()) {
      const figure = collection.drawn.get(key);
      if (figure === undefined) {
        continue;
      }
      collection.surrender(key);
      if (figure.kind === EntityKind.Player) {
        this.offer(figure, key, this.book.entities(keyMapIndex(key)).get(key));
      } else {
        figure.sprite.destroy();
      }
      return;
    }
  }

  /**
   * Holds a figure.
   *
   * @param figure The figure.
   * @param key Its key in the stream it left.
   * @param state Its last state, for its name and the plate drawn over it while held; undefined when none is known.
   */
  private offer(figure: DrawnCharacter, key: number, state: EntityState | undefined): void {
    this.hold.offer(figure, figure.x, figure.y, figure.vx, figure.vy, this.options.now(), key, state === undefined ? null : nameOf(state));
    if (state !== undefined) {
      this.heldStates.set(figure, { key, state });
    }
  }

  /**
   * Takes back a held figure for an entity a collection first draws, by its key, else its name; a moving one only once the
   * collection holds two records of it, so the figure goes on along the path its hold carried it.
   *
   * @param key The entity's key.
   * @param state The entity.
   * @param depth How many records the collection's interpolator holds of it.
   * @returns The figure, `CLAIM_LATER`, or null when none is held for it.
   */
  private claimAt(key: number, state: EntityState, depth: number): Readonly<Claimed<DrawnCharacter>> | typeof CLAIM_LATER | null {
    const name = nameOf(state);
    const now = this.options.now();
    const moving = this.hold.moving(key, name, now);
    if (moving === null) {
      return null;
    }
    if (moving && depth < 2) {
      return CLAIM_LATER;
    }
    return this.claimed(this.hold.claim(key, name, now));
  }

  /**
   * Forgets the state kept for a figure a collection took back.
   *
   * @param claimed What the hold gave back.
   * @returns The same.
   */
  private claimed(claimed: Readonly<Claimed<DrawnCharacter>> | null): Readonly<Claimed<DrawnCharacter>> | null {
    if (claimed !== null) {
      this.heldStates.delete(claimed.figure);
    }
    return claimed;
  }
}

/**
 * The name key of a player, or null for any other entity, which never crosses.
 *
 * @param state The entity.
 * @returns The key.
 */
function nameOf(state: EntityState): string | null {
  return state.kind === EntityKind.Player ? nameKey(state.name.first, state.name.family) : null;
}
